/**
 * Shared across every instance and every re-render of the cart drawer: the
 * drawer is morphed on each cart update, which throws away the element (and an
 * instance-level cache with it). Keyed by request URL, one per seed product, so
 * a cart that gains an item reuses the requests already made for the others.
 * @type {Map<string, Promise<string | null>>}
 */
const responseCache = new Map();

/**
 * @param {string} url
 * @returns {Promise<string | null>}
 */
function loadRecommendationsHtml(url) {
  const cached = responseCache.get(url);
  if (cached) return cached;

  const pending = fetch(url)
    .then((response) => (response.ok ? response.text() : null))
    .catch((error) => {
      console.error('Cart recommendations error:', error.message);
      return null;
    })
    .then((html) => {
      // A failure must not be remembered: caching the null would keep the block
      // empty for the rest of the session over one flaky request.
      if (html == null) responseCache.delete(url);
      return html;
    });

  responseCache.set(url, pending);
  return pending;
}

/**
 * @param {string} html
 * @returns {Element[]} the recommendation cards found in the section response
 */
function parseCards(html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const results = parsed.querySelector('[data-cart-recommendations-results]');

  return results ? Array.from(results.querySelectorAll('.cart-recommendation-card')) : [];
}

/** @param {() => void} callback */
function scheduleIdle(callback) {
  return window.requestIdleCallback ? requestIdleCallback(callback, { timeout: 2000 }) : setTimeout(callback, 200);
}

/** @param {number} handle */
function cancelIdle(handle) {
  window.cancelIdleCallback ? cancelIdleCallback(handle) : clearTimeout(handle);
}

class CartRecommendations extends HTMLElement {
  /** @type {number | null} */
  #idleHandle = null;

  /*
    The block lives inside the cart drawer, a <dialog> that is closed on load —
    so it never intersects until the customer opens the minicart. Gating the
    fetch on intersection meant the request only started at the exact moment the
    content was needed, and the customer watched it load. The fetch is kicked off
    on idle instead, while the drawer is still closed; this observer stays only
    as an accelerator, for when the drawer is opened before idle time arrives.
  */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 200px 0px' }
  );

  /*
    What is on screen is a function of three attributes, and the drawer's morph
    rewrites all of them on a cart update. A new seed product invalidates the
    fetched cards, so it refetches; anything else — the cart's contents changing
    the filter, or `hidden` being copied back over a block we had revealed — is
    just a re-render of what is already in hand.
  */
  #mutationObserver = new MutationObserver((records) => {
    const cartChanged = records.some((record) =>
      ['data-seed-product-ids', 'data-cart-product-ids'].includes(record.attributeName ?? '')
    );

    if (cartChanged) {
      this.#load();
      return;
    }

    this.#render();
  });

  /** @type {Element[] | null} */
  #cards = null;

  connectedCallback() {
    // Only the fetched sources (complementary, related) carry a URL; a
    // collection source is rendered server-side and needs nothing from here.
    if (!this.dataset.url) return;

    this.#idleHandle = scheduleIdle(() => this.#load());
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-seed-product-ids', 'data-cart-product-ids', 'hidden'],
    });
  }

  disconnectedCallback() {
    if (this.#idleHandle != null) cancelIdle(this.#idleHandle);
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * One request per seed product: the recommendations API takes a single
   * product, and the block covers the whole cart.
   * @returns {string[]}
   */
  #requestUrls() {
    const { seedProductIds, url } = this.dataset;
    if (!url || !seedProductIds) return [];

    return seedProductIds
      .split(',')
      .filter(Boolean)
      .map((id) => `${url}&product_id=${id}`);
  }

  async #load() {
    const requestUrls = this.#requestUrls();

    if (requestUrls.length === 0) {
      this.#cards = [];
      this.#render();
      return;
    }

    // In parallel, and deduplicated by the shared cache: two drawers on the page
    // asking for the same seed share a single request.
    const responses = await Promise.all(requestUrls.map(loadRecommendationsHtml));

    // The cart can change while the requests are in flight, which changes the
    // seeds — don't paint a response set that no longer matches this element.
    if (!this.isConnected || this.#requestUrls().join(',') !== requestUrls.join(',')) return;

    // Seed order decides which suggestions come first; #render dedupes.
    this.#cards = responses.flatMap((html) => (html == null ? [] : parseCards(html)));
    this.#render();
  }

  /**
   * Single place that decides what is on screen, derived only from the cards
   * last fetched and the element's current attributes. Safe to call at any time
   * and as often as needed.
   */
  #render() {
    const list = this.querySelector('[data-cart-recommendations-list]');
    if (this.#cards == null || !list) return;

    const inCart = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
    const max = parseInt(this.dataset.max || '4', 10);
    const seen = new Set();
    const visible = [];

    for (const card of this.#cards) {
      const id = card instanceof HTMLElement ? card.dataset.productId || '' : '';
      if (!id || seen.has(id) || inCart.has(id)) continue;

      seen.add(id);
      // Cloned so the fetched set stays intact for the next render.
      visible.push(card.cloneNode(true));
      if (visible.length >= max) break;
    }

    if (visible.length === 0) {
      this.#hide();
      return;
    }

    list.replaceChildren(...visible);
    this.#show();
  }

  #hide() {
    if (!this.hidden) this.hidden = true;
  }

  #show() {
    if (this.hidden) this.hidden = false;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
